import { useTranslation } from "react-i18next";

import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { FamilyMemberResponseDto, UserAccountDto } from "../api/client-axios";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { RootState } from "../app/store";
import { RouteNames } from "../routes";
import { formatPhone } from "../utils/formatPhone";
import {
    clearErrorNoOrgFamilyMembers,
    deleteNoOrgFamilyMember,
    fetchNoOrgFamilyMembers,
    selectNoOrgFamilyMember,
    startDeleteNoOrgFamilyMember, updateSearchTermNoOrgFamilyMember
} from "../features/familyMembersNoOrgSlice";

import { CrudModals } from "../components/CrudModals";
import { CrudToolbar } from "../components/CrudToolbar";
import { EmptyState } from "../components/EmptyState";
import { TableSkeleton } from "../components/TableSkeleton";

export const FamilyMemberIndividualsPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const familyMembers = useAppSelector((state: RootState) => state.familyMembersNoOrg.familyMembers);
    const selectedFamilyMember = useAppSelector((state: RootState) => state.familyMembersNoOrg.selectedFamilyMember);
    const searchTerm = useAppSelector((state: RootState) => state.familyMembersNoOrg.searchTerm);
    const countries = useAppSelector((state: RootState) => state.country.countries);
    const status = useAppSelector((state: RootState) => state.familyMembersNoOrg.status);
    const error = useAppSelector((state: RootState) => state.familyMembersNoOrg.error);
    const navigate = useNavigate();
    const params = useParams();

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchNoOrgFamilyMembers({ }))
        }

    });

    if (params.familyMemberId !== undefined) {
        return <Outlet />
    }

    const onWarnDeleteFamilyMember = () => {
        if (selectedFamilyMember != null) {
            dispatch(startDeleteNoOrgFamilyMember());
        }
    }

    
    const onEditFamilyMember = () => {
        if (selectedFamilyMember != null) {
            navigate(`/${RouteNames.individuals}/familyMembers/${selectedFamilyMember.id}`);
        }
    }

    const onReload = () => {

        dispatch(fetchNoOrgFamilyMembers({ }));
    }

    const onSearch = () => {
        dispatch(fetchNoOrgFamilyMembers({  q: searchTerm }));
    }

    const onRow = (familyMember: FamilyMemberResponseDto, index: number | undefined) => {
        return {
            onClick: () => {
                dispatch(selectNoOrgFamilyMember(familyMember))
            }, // click row
        }
    }

    const onDeleteOrganizationAdmin = () => {
        if (selectedFamilyMember != null) {
            dispatch(deleteNoOrgFamilyMember({
                familyMemberId: selectedFamilyMember.id!
            }));
        }
    }

    const getRowClassName = (familyMember: FamilyMemberResponseDto, index: number) => {
        return selectedFamilyMember != null && selectedFamilyMember.id === familyMember.id ? 'bg-selected' : '';
    }

    const columns: ColumnsType<FamilyMemberResponseDto> = [
        {

            // width: '100%',
            title: t("column.firstName"),
            dataIndex: 'firstName',
            key: 'firstName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.lastName"),
            dataIndex: 'lastName',
            key: 'lastName',
            sorter: (a, b) => a.firstName! > b.firstName! ? -1 : 1,
        },
        {

            //width: '100%',
            title: t("column.email"),
            dataIndex: 'userAccount',
            key: 'email',
            sorter: (a, b) => a.userAccount?.emailAddress! > b.userAccount?.emailAddress! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => userAccount?.emailAddress
        },
        {

            //width: '100%',
            title: t("column.phone"),
            dataIndex: 'userAccount',
            key: 'phone',
            sorter: (a, b) => a.userAccount?.mobileNumber! > b.userAccount?.mobileNumber! ? -1 : 1,
            render: (userAccount?: UserAccountDto) => formatPhone(userAccount?.mobileNumber, countries)
        },

    ];

    //todo implement server side paging and sorting
    const content = status === 'loading' || status === 'idle'
        ? <TableSkeleton />
        : <>
            <CrudToolbar
                hasSelection={selectedFamilyMember != null}
                canEdit={selectedFamilyMember?.id != null}
                selectionLabel={[selectedFamilyMember?.firstName, selectedFamilyMember?.lastName].filter(Boolean).join(" ")}
                onDelete={onWarnDeleteFamilyMember}
                onEdit={onEditFamilyMember}
                onReload={onReload}
                onClearSelection={() => dispatch(selectNoOrgFamilyMember(undefined))}
                search={{
                    placeholder: t("placeholder.searchPeople"),
                    value: searchTerm,
                    onChange: value => dispatch(updateSearchTermNoOrgFamilyMember(value)),
                    onSearch
                }}
            />
            <div className="card card--flush">
                <Table
                    rowKey={(familyMember: FamilyMemberResponseDto) => familyMember.id!}
                    rowClassName={getRowClassName}
                    onRow={onRow}
                    dataSource={familyMembers}
                    columns={columns}
                    locale={{ emptyText: <EmptyState /> }}
                    pagination={{ hideOnSinglePage: true, defaultPageSize: 12, showSizeChanger: false }}
                />
            </div>

            <CrudModals
                status={status}
                error={error}
                entityKey="familyMember"
                deleteTarget={[selectedFamilyMember?.firstName, selectedFamilyMember?.lastName].filter(Boolean).join(" ")}
                onDismiss={() => dispatch(clearErrorNoOrgFamilyMembers())}
                onConfirmDelete={onDeleteOrganizationAdmin}
            />
        </>

    return content;
}
